import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatMoney, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import EstadoPagoBadge from "@/components/EstadoPagoBadge";
import PagosPanel from "@/components/PagosPanel";
import {
  Wallet, Search, ChevronLeft, User as UserIcon, FileText,
} from "lucide-react";

export default function CuentaCorriente() {
  const { cid: urlCid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ total_deuda_global: 0, clientes: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(urlCid || null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadList = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cuenta-corriente/clientes");
      setData(r.data);
    } finally { setLoading(false); }
  };

  const loadDetail = async (cid) => {
    setLoadingDetail(true);
    try {
      const r = await api.get(`/cuenta-corriente/cliente/${cid}`);
      setDetail(r.data);
    } finally { setLoadingDetail(false); }
  };

  useEffect(() => { loadList(); }, []);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId]);
  useEffect(() => { setSelectedId(urlCid || null); }, [urlCid]);

  const filtered = data.clientes.filter((c) => {
    if (!q.trim()) return true;
    const qn = q.toLowerCase();
    return (c.nombre || "").toLowerCase().includes(qn) ||
           (c.email || "").toLowerCase().includes(qn) ||
           (c.telefono || "").toLowerCase().includes(qn);
  });

  const onPedidoChange = (updated) => {
    if (!detail) return;
    // replace pedido in detail.pedidos, recompute deuda; if saldo=0 remove
    const newPedidos = detail.pedidos
      .map((p) => (p.id === updated.id ? updated : p))
      .filter((p) => p.cuenta_corriente && p.estado !== "CANCELADO");
    const deuda = newPedidos.reduce((s, p) => s + Number(p.saldo_pendiente || 0), 0);
    setDetail({ ...detail, deuda_total: Math.round(deuda * 100) / 100, pedidos: newPedidos });
    // refresh global list silently
    loadList();
  };

  if (selectedId && detail) {
    return (
      <div>
        <button
          type="button"
          onClick={() => { setSelectedId(null); navigate("/admin/cuenta-corriente"); }}
          className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#C85A32] mb-4"
          data-testid="cc-back"
        >
          <ChevronLeft className="h-4 w-4" /> Volver a clientes
        </button>
        <PageHeader
          eyebrow="Cuenta Corriente"
          title={detail.cliente.nombre}
          subtitle={`Pedidos pendientes de pago de ${detail.cliente.email}`}
        />
        <Card className="p-5 border-[#E5E0D8] rounded-2xl bg-[#F4EAE6] mb-6 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A64A29] font-semibold">Deuda total</div>
            <div className="font-display font-bold text-4xl text-[#A64A29] mt-1" data-testid="cc-deuda-total">
              {formatMoney(detail.deuda_total)}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-[#A64A29]/70">Pedidos</div>
            <div className="font-mono font-bold text-2xl text-[#A64A29]">{detail.pedidos.length}</div>
          </div>
        </Card>

        {loadingDetail && <div className="text-[#8C8C8C]">Cargando...</div>}

        <div className="space-y-4">
          {detail.pedidos.length === 0 && (
            <Card className="p-10 text-center border-[#E5E0D8] rounded-2xl bg-white">
              <Wallet className="h-10 w-10 mx-auto text-[#C85A32] opacity-50 mb-3" />
              <div className="font-display font-semibold text-lg">Sin pedidos pendientes</div>
              <p className="text-sm text-[#595959] mt-1">El cliente está al día.</p>
            </Card>
          )}
          {detail.pedidos.map((p) => (
            <Card key={p.id} className="p-4 sm:p-5 border-[#E5E0D8] rounded-2xl bg-white" data-testid={`cc-pedido-${p.pedido_id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/admin/pedidos/${p.id}`} className="font-mono font-semibold text-[#C85A32] hover:underline">
                    {p.pedido_id}
                  </Link>
                  <EstadoPagoBadge estado={p.estado_pago} />
                  <span className="text-xs text-[#8C8C8C]">{formatDate(p.fecha)}</span>
                </div>
                <Link
                  to={`/admin/pedidos/${p.id}`}
                  className="text-xs text-[#595959] hover:text-[#C85A32] inline-flex items-center gap-1"
                >
                  <FileText className="h-3.5 w-3.5" /> Ver pedido
                </Link>
              </div>
              <PagosPanel pedido={p} onChange={onPedidoChange} />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Finanzas"
        title="Cuenta Corriente"
        subtitle="Clientes con deuda pendiente. Tocá un cliente para registrar pagos."
      />

      <Card className="p-5 sm:p-6 border-[#E5E0D8] rounded-2xl bg-gradient-to-br from-[#F4EAE6] to-[#FBF1ED] mb-5">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[#A64A29] font-semibold">Total deuda global</div>
        <div className="font-display font-bold text-5xl text-[#A64A29] mt-1" data-testid="cc-total-global">
          {formatMoney(data.total_deuda_global)}
        </div>
        <div className="text-sm text-[#A64A29]/70 mt-1">{data.clientes.length} cliente{data.clientes.length === 1 ? "" : "s"}</div>
      </Card>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C8C8C]" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar cliente..."
          className="pl-9 h-11 bg-white"
          data-testid="cc-search"
        />
      </div>

      {loading ? (
        <div className="text-[#8C8C8C]">Cargando...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center border-[#E5E0D8] rounded-2xl bg-white">
          <Wallet className="h-10 w-10 mx-auto text-[#C85A32] opacity-50 mb-3" />
          <div className="font-display font-semibold text-lg">Sin deudas pendientes</div>
          <p className="text-sm text-[#595959] mt-1">Todos los clientes están al día.</p>
        </Card>
      ) : (
        <div className="grid gap-2">
          {filtered.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className="text-left bg-white border border-[#E5E0D8] rounded-xl p-4 hover:border-[#C85A32] hover:bg-[#FBF8F2] transition-colors flex items-center justify-between gap-3"
              data-testid={`cc-cli-${c.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 rounded-full bg-[#F4EAE6] text-[#C85A32] flex items-center justify-center shrink-0">
                  <UserIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-[#1A1A1A] truncate">{c.nombre}</div>
                  <div className="text-[12px] text-[#8C8C8C] truncate">{c.email}</div>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-[#A64A29]">{formatMoney(c.deuda_total)}</div>
                <div className="text-[11px] text-[#8C8C8C]">{c.pedidos_con_deuda} pedido{c.pedidos_con_deuda === 1 ? "" : "s"}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
